import React from 'react';
import { t, defineMessages } from '@sm/intl';
import { Box } from '@wds/box';
import EndpageHeader from '~app/components/EndpageHeader';
import { COPY as SIGN_UP_COPY } from '~app/components/RebrandEpFormCore/index';
import { COPY as PRODUCT_COPY } from '~app/pages/SurveyThanks/RebrandEpForm/index';
import FortuneCallout, { ColorProp } from '~app/components/FortuneCallout/FortuneCallout';
import SplitHero from '~app/components/SplitHero';
import ColoredLogoWithText from '~app/pages/SurveyThanks/BuildWithAI/ColoredLogoWithText';
import { DareToAskProps } from '~helpers/types';

export const DARE_TO_ASK_COPY = defineMessages({
  STATISTIC_HEADER: {
    id: 'DareToAsk.StatisticHeader',
    defaultMessage: "We answer 20M questions a day.<br/>What's yours?",
    description: '[Type: label][Vis: high] - A Header to encourage users to sign up',
  },
  STATISTIC_SUBHEADER: {
    id: 'DareToAsk.StatisticSubHeader',
    defaultMessage:
      'SurveyMonkey helps answer 20M questions a day, from more than 300,000 global organizations. If you care enough to ask, we’re here to make it easy.',
    description: '[Type: label][Vis: low] - A sub-header to encourage users to sign up',
  },
  PROVOCATIVE_HEADER: {
    id: 'DareToAsk.ProvocativeHeader',
    defaultMessage: 'Dare to ask the questions that matter',
    description: '[Type: label][Vis: high] - A Header to encourage users to sign up',
  },
  PROVOCATIVE_SUBHEADER_SIGNUP: {
    id: 'DareToAsk.ProvocativeSubHeaderSignUp',
    defaultMessage:
      'Caring about your customers and employees means daring to ask the questions that matter. Create an account and start getting insights in as little as an hour.',
    description: '[Type: label][Vis: low] - A sub-header to encourage users to sign up',
  },
  PROVOCATIVE_SUBHEADER_LEARN_MORE: {
    id: 'DareToAsk.ProvocativeSubHeaderLearnMore',
    defaultMessage:
      'Caring about your customers and employees means daring to ask the questions that matter. See how to get the insights you need to drive your business forward.',
    description: '[Type: label][Vis: low] - A sub-header to encourage users to sign up',
  },
  ALT_FORTUNE_CALLOUT_COPY: {
    id: 'DareToAsk.FortuneCalloutCopy',
    defaultMessage: '42 million+ people are using SurveyMonkey globally',
    description: '[Type: Header][Vis: low] - A promotional statistic designed to show corporate trust and prestige',
  },
});

const DARE_TO_ASK_V2_PRACTICAL_COPY = {
  header: 'Check out workplace trends to watch',
  subHeader:
    'From work-life balance to employee confessions, SurveyMonkey has the latest data on what’s really going on in today’s workplace.',
};

const BWAI_TEST_INSIGHT_COPY = {
  header: 'Use AI to create surveys and get insights fast',
  subHeader: 'Need to send a survey but don’t know where to start? No problem.',
};

const BWAI_TEST_CREATION_COPY = {
  header: 'Generate surveys in as little as 30 seconds',
  subHeader: 'Need to send a survey but don’t know where to start? No problem.',
};

const ENDPAGE_V1_COPY = {
  header: 'Power real results for your business',
  subHeader:
    'Create your own survey in under a minute with AI. Reach anyone, anywhere, and get the insights you need to move your business forward. For real.',
};

const CURIOSITY_CON_COPY = {
  header: "You're invited to CuriosityCon",
  subHeader:
    'Join us virtually on May 13 to learn from top brands, level up your survey skills, and see new product innovations.',
};

type copyObject = { [key: string]: string };

function DareToAsk({
  utSource2,
  splitHeroDarkBackground = false,
  splitHeroCTA = false,
  splitHeroPrimaryCTA = '',
  splitHeroSecondaryCTA = '',
  splitHeroImage = 'dareToAskText',
  fortuneBannerDarkBackground = false,
  fortuneBannerLogoWall = false,
  fortuneBannerCTA = false,
  buildWithAI = false,
  rebrandEP2025 = false,
  curiosityCon = false,
}: DareToAskProps): React.ReactElement {
  const utSource = 'survey-thanks';

  const boldHeaderMapping: copyObject = {
    DareToAskPractical: DARE_TO_ASK_V2_PRACTICAL_COPY.header,
    DareToAskPracticalUk: DARE_TO_ASK_V2_PRACTICAL_COPY.header,
    BwAiGIFInsight: BWAI_TEST_INSIGHT_COPY.header,
    BwAiGIFCreation: BWAI_TEST_CREATION_COPY.header,
    DareToAskV3SignUp: DARE_TO_ASK_V2_PRACTICAL_COPY.header,
    DareToAskCreate: DARE_TO_ASK_V2_PRACTICAL_COPY.header,
    curiosityConEp: CURIOSITY_CON_COPY.header,
    rebrandEP2025: ENDPAGE_V1_COPY.header,
  };
  const boldHeader = boldHeaderMapping[utSource2] ?? t(DARE_TO_ASK_COPY.STATISTIC_HEADER, {}, { html: true });

  const subHeaderMapping: copyObject = {
    DareToAskPractical: DARE_TO_ASK_V2_PRACTICAL_COPY.subHeader,
    DareToAskPracticalUk: DARE_TO_ASK_V2_PRACTICAL_COPY.subHeader,
    BwAiGIFInsight: BWAI_TEST_INSIGHT_COPY.subHeader,
    BwAiGIFCreation: BWAI_TEST_CREATION_COPY.subHeader,
    dareToAskV3SignUp: DARE_TO_ASK_V2_PRACTICAL_COPY.subHeader,
    dareToAskCreate: DARE_TO_ASK_V2_PRACTICAL_COPY.subHeader,
    curiosityConEp: CURIOSITY_CON_COPY.subHeader,
    rebrandEP2025: ENDPAGE_V1_COPY.subHeader,
  };

  const subHeader = subHeaderMapping[utSource2] ?? t(DARE_TO_ASK_COPY.PROVOCATIVE_SUBHEADER_LEARN_MORE);

  const FortuneBannerCopy = [
    'DareToAskPractical',
    'DareToAskPracticalUk',
    'BwAiGIFInsight',
    'BwAiGIFCreation',
    'DareToAskV3SignUp',
    'DareToAskCreate',
    'rebrandEP2025',
    'curiosityConEp',
  ].includes(utSource2)
    ? t(DARE_TO_ASK_COPY.ALT_FORTUNE_CALLOUT_COPY)
    : t(SIGN_UP_COPY.FORTUNE_CALLOUT_COPY);

  const HERO_FORM_CTA = {
    ctaFormEmail: t(SIGN_UP_COPY.HERO_FORM_CTA_EMAIL_ADDRESS),
    ctaFormEmailPlaceholder: t(SIGN_UP_COPY.HERO_FORM_CTA_EMAIL_ADDRESS_PLACEHOLDER),
    ctaFormNewsletterOptOut: t(SIGN_UP_COPY.HERO_FORM_CTA_NEWSLETTER_OPTOUT),
    ctaFormPassword: t(SIGN_UP_COPY.HERO_FORM_CTA_PASSWORD),
    ctaFormPasswordPlaceholder: t(SIGN_UP_COPY.HERO_FORM_CTA_PASSWORD_PLACEHOLDER),
    ctaFormPrivacyConsent: t(SIGN_UP_COPY.HERO_FORM_CTA_PRIVACY_CONSENT, {}, { html: true }),
    ctaFormSubmit: t(SIGN_UP_COPY.HERO_FORM_CTA_SUBMIT),
    disclaimer: t(SIGN_UP_COPY.HERO_FORM_CTA_DISCLAIMER),
    utSource,
    utSource2,
  };

  const determineColor = (source: boolean, fortuneBannerDarkBg: boolean): ColorProp => {
    if (source) {
      return 'greyOil';
    }
    return fortuneBannerDarkBg ? 'vine' : 'canvas';
  };

  return (
    <>
      {buildWithAI || curiosityCon ? (
        <Box style={{ backgroundColor: curiosityCon ? '#00BF6F' : '#224F3C' }}>
          <ColoredLogoWithText utSource2={utSource2} utSource={utSource} buildWithAI />
        </Box>
      ) : (
        <EndpageHeader copy={t(PRODUCT_COPY.ENDPAGE_HEADER_COPY)} hasLogo headerColor="sabaeus" />
      )}

      <SplitHero
        utSource={utSource}
        utSource2={utSource2}
        boldHeader={boldHeader}
        subHeader={subHeader}
        formCopy={HERO_FORM_CTA}
        splitHeroDarkBackground={splitHeroDarkBackground}
        splitHeroCTA={splitHeroCTA}
        splitHeroPrimaryCTA={splitHeroPrimaryCTA}
        splitHeroSecondaryCTA={splitHeroSecondaryCTA}
        splitHeroImage={splitHeroImage}
        buildWithAI={buildWithAI}
        curiosityCon={curiosityCon}
        rebrandEP2025={rebrandEP2025}
      />
      <FortuneCallout
        showLogoWall={fortuneBannerLogoWall}
        copy={FortuneBannerCopy}
        color={determineColor(curiosityCon, fortuneBannerDarkBackground)}
        ctaText={SIGN_UP_COPY.FORTUNE_CALLOUT_CTA}
        ctaUsage="sign-up tertiary"
        utSource={utSource}
        utSource2={utSource2}
        hideCTAs={!fortuneBannerCTA}
      />
    </>
  );
}

export default DareToAsk;
